import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { X, Mail, Phone, Building2, Calendar, DollarSign, User, Briefcase, Plus, Edit, Trash2, CheckCircle, Circle, ExternalLink, Users as UsersIcon } from 'lucide-react';
import AddInvoiceModal from './AddInvoiceModal';
import AddTaskModal from './AddTaskModal';
import { showToast } from '../../utils/toast';
import AddAssetModal from './AddAssetModal';
import EditDigitalPropertiesModal from './EditDigitalPropertiesModal';
import ClientMessagingTab from './ClientMessagingTab';
import ClientSupportTab from './ClientSupportTab';
import AdminBillingSection from './AdminBillingSection';
import AdminContractSection from './AdminContractSection';
import AdminPerformanceSection from './AdminPerformanceSection';
import { formatDateEST, formatDateTimeEST } from '../../utils/timezone';
import { useRole, PERMISSIONS } from '../../context/RoleContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ClientDetailDrawer({ client, isOpen, onClose, onUpdate, isAdmin = true }) {
  const { hasPermission, isAgencyDirector, isOA } = useRole();
  const [clientStatus, setClientStatus] = useState(client?.client_status || 'onboarding');
  const [contractStatus, setContractStatus] = useState(client?.contract_status || 'draft');
  const [noteText, setNoteText] = useState('');
  const [notes, setNotes] = useState(client?.internal_notes || []);
  const [invoices, setInvoices] = useState(client?.invoices || []);
  const [tasks, setTasks] = useState(client?.onboarding_tasks || []);
  const [digitalProperties, setDigitalProperties] = useState(client?.digital_properties || {});
  const [assets, setAssets] = useState(client?.assets || []);
  const [auditLogs, setAuditLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Modal states
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [showPropertiesModal, setShowPropertiesModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  
  // Client Team state (read-only)
  const [clientTeam, setClientTeam] = useState({
    csm: null,
    marketing_specialists: [],
    operations_assistants: []
  });

  useEffect(() => {
    if (isOpen && client) {
      fetchClientDetail();
      fetchClientTeam();
    }
  }, [isOpen, client]);

  const fetchClientDetail = async () => {
    if (!client) return;
    
    try {
      setLoadingDetail(true);
      const response = await fetch(`${API}/api/admin/clients/${client.client_id}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const clientData = data.client;
        setNotes(clientData.internal_notes || []);
        setClientStatus(clientData.client_status || 'onboarding');
        setContractStatus(clientData.contract_status || 'draft');
        setInvoices(clientData.invoices || []);
        setTasks(clientData.onboarding_tasks || []);
        setDigitalProperties(clientData.digital_properties || {});
        setAssets(clientData.assets || []);
        setAuditLogs(data.audit_logs || []);
        setAssignedCSM(clientData.assigned_csm || null);
      }
    } catch (error) {
      console.error('Error fetching client detail:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleUpdateClient = async (updates) => {
    try {
      setSaving(true);
      const response = await fetch(`${API}/api/admin/clients/${client.client_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        await fetchClientDetail();
        onUpdate();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Failed to update client: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating client:', error);
      alert('Failed to update client');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleTask = async (taskId, completed) => {
    try {
      setSaving(true);
      const response = await fetch(`${API}/api/admin/clients/${client.client_id}/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ completed })
      });
      
      if (response.ok) {
        await fetchClientDetail();
      }
    } catch (error) {
      console.error('Error toggling task:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      setSaving(true);
      const response = await fetch(`${API}/api/admin/clients/${client.client_id}/tasks/${taskId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        await fetchClientDetail();
      }
    } catch (error) {
      console.error('Error deleting task:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkInvoicePaid = async (invoiceId) => {
    try {
      setSaving(true);
      const response = await fetch(`${API}/api/admin/clients/${client.client_id}/invoices/${invoiceId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: 'paid' })
      });
      
      if (response.ok) {
        await fetchClientDetail();
      }
    } catch (error) {
      console.error('Error updating invoice:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    
    try {
      setSaving(true);
      const response = await fetch(`${API}/api/admin/clients/${client.client_id}/invoices/${invoiceId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        await fetchClientDetail();
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;
    
    try {
      setSaving(true);
      const response = await fetch(`${API}/api/admin/clients/${client.client_id}/assets/${assetId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        await fetchClientDetail();
      }
    } catch (error) {
      console.error('Error deleting asset:', error);
    } finally {
      setSaving(false);
    }
  };


  // Fetch client team (read-only)
  const fetchClientTeam = async () => {
    try {
      const response = await fetch(`${API}/api/admin/clients/${client.client_id}/team`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setClientTeam({
          csm: data.csm || null,
          marketing_specialists: data.marketing_specialists || [],
          operations_assistants: data.operations_assistants || []
        });
      }
    } catch (error) {
      console.error('Error fetching client team:', error);
    }
  };


  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    
    try {
      setSaving(true);
      const response = await fetch(`${API}/api/admin/clients/${client.client_id}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: noteText })
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotes([...notes, data.note]);
        setNoteText('');
        await fetchClientDetail();
      }
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getContractStatusColor = (status) => {
    switch(status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'sent_to_client': return 'bg-blue-100 text-blue-800';
      case 'viewed': return 'bg-purple-100 text-purple-800';
      case 'signed_pending_payment': return 'bg-yellow-100 text-yellow-800';
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-orange-100 text-orange-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'expired': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getInvoiceStatusColor = (status) => {
    switch(status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'unpaid': return 'bg-yellow-100 text-yellow-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      case 'refunded': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateBillingSummary = () => {
    const totalInvoiced = invoices.reduce((sum, inv) => sum + inv.amount, 0);
    const totalPaid = invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.amount, 0);
    const totalOverdue = invoices.filter(inv => inv.status === 'overdue').reduce((sum, inv) => sum + inv.amount, 0);
    return { totalInvoiced, totalPaid, totalOverdue };
  };

  const getTaskProgress = () => {
    if (tasks.length === 0) return 0;
    const completed = tasks.filter(t => t.completed).length;
    return Math.round((completed / tasks.length) * 100);
  };

  const groupAssetsByType = () => {
    const grouped = {};
    assets.forEach(asset => {
      if (!grouped[asset.asset_type]) {
        grouped[asset.asset_type] = [];
      }
      grouped[asset.asset_type].push(asset);
    });
    return grouped;
  };

  if (!isOpen || !client) return null;

  const billingSummary = calculateBillingSummary();
  const taskProgress = getTaskProgress();
  const groupedAssets = groupAssetsByType();
  const lastInvoice = invoices.length > 0 ? invoices[invoices.length - 1] : null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      
      <div className="absolute right-0 top-0 bottom-0 w-full md:max-w-4xl bg-white shadow-xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10 shadow-sm">
          <div className="flex-1 min-w-0 pr-4">
            <div className="text-sm font-mono font-semibold text-blue-600 mb-1">
              {client.short_id || client.client_id}
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
              {client.business_name}
            </h2>
            <p className="text-sm text-gray-600 truncate">
              {client.primary_contact_first_name} {client.primary_contact_last_name}
            </p>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={onClose} className="flex-shrink-0 touch-manipulation">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
          {/* Client Team Section - Read Only */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <UsersIcon className="h-5 w-5" />
                Client Team
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* CSM */}
                <div>
                  <Label className="text-sm font-semibold mb-2 block">
                    Client Success Manager (CSM)
                  </Label>
                  {clientTeam.csm ? (
                    <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                        {clientTeam.csm.full_name?.charAt(0) || 'C'}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {clientTeam.csm.full_name || clientTeam.csm.username}
                        </div>
                        <div className="text-xs text-gray-600">
                          {clientTeam.csm.email || ''}
                        </div>
                        <div className="text-xs text-blue-600 font-medium">Client Success Manager</div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                      No CSM assigned yet
                    </div>
                  )}
                </div>

                {/* Marketing Specialists */}
                <div>
                  <Label className="text-sm font-semibold mb-2 block">
                    Marketing Specialists
                  </Label>
                  {clientTeam.marketing_specialists.length > 0 ? (
                    <div className="space-y-2">
                      {clientTeam.marketing_specialists.map(ms => (
                        <div key={ms.username} className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                          <div className="h-10 w-10 rounded-full bg-purple-500 flex items-center justify-center text-white font-semibold">
                            {ms.full_name?.charAt(0) || 'M'}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{ms.full_name || ms.username}</div>
                            <div className="text-xs text-gray-600">{ms.email || ''}</div>
                            <div className="text-xs text-purple-600 font-medium">Marketing Specialist</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                      No Marketing Specialists assigned
                    </div>
                  )}
                </div>

                {/* Operations Assistants */}
                <div>
                  <Label className="text-sm font-semibold mb-2 block">
                    Operations Assistants
                  </Label>
                  {clientTeam.operations_assistants.length > 0 ? (
                    <div className="space-y-2">
                      {clientTeam.operations_assistants.map(oa => (
                        <div key={oa.username} className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="h-10 w-10 rounded-full bg-green-500 flex items-center justify-center text-white font-semibold">
                            {oa.full_name?.charAt(0) || 'O'}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{oa.full_name || oa.username}</div>
                            <div className="text-xs text-gray-600">{oa.email || ''}</div>
                            <div className="text-xs text-green-600 font-medium">Operations Assistant</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                      No Operations Assistants assigned
                    </div>
                  )}
                </div>

                {/* Management Note */}
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> Team assignments are managed in Settings → Staff Management
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contract Management Section - View for all, manage for AD only */}
          {hasPermission(PERMISSIONS.VIEW_CONTRACTS) && (
            <AdminContractSection
              clientId={client.client_id}
              currentStatus={contractStatus}
              onUpdate={() => {
                fetchClientDetail();
                if (onUpdate) onUpdate();
              }}
              readOnly={!hasPermission(PERMISSIONS.MANAGE_CONTRACTS)}
            />
          )}

          {/* Billing & Invoices Section - View for all, manage for AD/CSM */}
          {hasPermission(PERMISSIONS.VIEW_BILLING) && (
            <AdminBillingSection
              clientId={client.client_id}
              onUpdate={() => {
                fetchClientDetail();
                if (onUpdate) onUpdate();
              }}
              readOnly={!hasPermission(PERMISSIONS.MANAGE_BILLING)}
            />
          )}

          {/* Performance Analytics Section */}
          <AdminPerformanceSection
            clientId={client.client_id}
            onUpdate={() => {
              fetchClientDetail();
              if (onUpdate) onUpdate();
            }}
          />

          {/* Messaging Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Messages</CardTitle>
            </CardHeader>
            <CardContent>
              <ClientMessagingTab
                clientId={client.client_id}
                clientInfo={{
                  business_name: client.business_name,
                  primary_contact_name: `${client.primary_contact_first_name} ${client.primary_contact_last_name}`,
                  assigned_staff: client.assigned_staff
                }}
              />
            </CardContent>
          </Card>

          {/* Invoices Section */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Invoices</CardTitle>
              {hasPermission(PERMISSIONS.MANAGE_BILLING) && (
                <Button size="sm" onClick={() => setShowInvoiceModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Invoice
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No invoices yet</p>
              ) : (
                <div className="space-y-2">
                  {invoices.map((invoice) => (
                    <div key={invoice.invoice_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">${invoice.amount.toFixed(2)}</p>
                          <span className={`px-2 py-0.5 rounded text-xs ${getInvoiceStatusColor(invoice.status)}`}>
                            {invoice.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{invoice.description}</p>
                        <p className="text-xs text-gray-500">
                          {formatDateEST(invoice.date)}
                          {invoice.due_date && ` • Due: ${formatDateEST(invoice.due_date)}`}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {invoice.status !== 'paid' && hasPermission(PERMISSIONS.MANAGE_BILLING) && (
                          <Button size="sm" variant="outline" onClick={() => handleMarkInvoicePaid(invoice.invoice_id)}>
                            Mark Paid
                          </Button>
                        )}
                        {hasPermission(PERMISSIONS.MANAGE_BILLING) && (
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteInvoice(invoice.invoice_id)}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Onboarding Checklist */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex-1">
                <CardTitle className="text-lg">Onboarding Checklist</CardTitle>
                <div className="mt-2">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full transition-all" 
                        style={{ width: `${taskProgress}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-700">{taskProgress}%</span>
                  </div>
                </div>
              </div>
              {hasPermission(PERMISSIONS.MANAGE_ONBOARDING_TASKS) && (
                <Button size="sm" onClick={() => setShowTaskModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Task
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {tasks.sort((a, b) => a.order - b.order).map((task) => (
                  <div key={task.task_id} className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded">
                    <button
                      onClick={() => handleToggleTask(task.task_id, !task.completed)}
                      className="mt-0.5 flex-shrink-0"
                      disabled={saving}
                    >
                      {task.completed ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <Circle className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${task.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                        {task.title}
                      </p>
                      {task.due_date && (
                        <p className="text-xs text-gray-500">
                          Due: {formatDateEST(task.due_date)}
                        </p>
                      )}
                      {task.completed && task.completed_by && (
                        <p className="text-xs text-gray-500">
                          Completed by {task.completed_by} on {formatDateEST(task.completed_at)}
                        </p>
                      )}
                      {task.notes && <p className="text-xs text-gray-600 mt-1">{task.notes}</p>}
                    </div>
                    {hasPermission(PERMISSIONS.MANAGE_ONBOARDING_TASKS) && (
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteTask(task.task_id)}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Digital Properties */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Digital Properties & Access</CardTitle>
              <Button size="sm" onClick={() => setShowPropertiesModal(true)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Website & Platform */}
              <div>
                <h4 className="font-semibold text-sm text-gray-700 mb-2">Website & Platform</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Website URL</p>
                    {digitalProperties.primary_website_url ? (
                      <a href={digitalProperties.primary_website_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                        {digitalProperties.primary_website_url}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <p className="text-gray-400">-</p>
                    )}
                  </div>
                  <div>
                    <p className="text-gray-500">CMS Platform</p>
                    <p className="text-gray-900">{digitalProperties.cms_platform || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Hosting Provider</p>
                    <p className="text-gray-900">{digitalProperties.hosting_provider || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Domain Registrar</p>
                    <p className="text-gray-900">{digitalProperties.domain_registrar || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Ad & Analytics IDs */}
              <div className="pt-3 border-t">
                <h4 className="font-semibold text-sm text-gray-700 mb-2">Ad & Analytics IDs</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Google Ads Account ID</p>
                    <p className="text-gray-900 font-mono text-xs">{digitalProperties.google_ads_account_id || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Meta Business Manager ID</p>
                    <p className="text-gray-900 font-mono text-xs">{digitalProperties.meta_business_manager_id || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Google Analytics 4 ID</p>
                    <p className="text-gray-900 font-mono text-xs">{digitalProperties.google_analytics_property_id || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">GTM Container ID</p>
                    <p className="text-gray-900 font-mono text-xs">{digitalProperties.google_tag_manager_container_id || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Login Info */}
              {(digitalProperties.login_url || digitalProperties.login_username || digitalProperties.login_notes) && (
                <div className="pt-3 border-t">
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Login & Access</h4>
                  <div className="space-y-2 text-sm">
                    {digitalProperties.login_url && (
                      <div>
                        <p className="text-gray-500">Login URL</p>
                        <a href={digitalProperties.login_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                          {digitalProperties.login_url}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    )}
                    {digitalProperties.login_username && (
                      <div>
                        <p className="text-gray-500">Username</p>
                        <p className="text-gray-900">{digitalProperties.login_username}</p>
                      </div>
                    )}
                    {digitalProperties.login_notes && (
                      <div>
                        <p className="text-gray-500">Notes</p>
                        <p className="text-gray-700 bg-gray-50 p-2 rounded text-xs">{digitalProperties.login_notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Assets Section */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Assets</CardTitle>
              {hasPermission(PERMISSIONS.MANAGE_ASSETS) && (
                <Button size="sm" onClick={() => setShowAssetModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Asset
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {assets.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No assets yet</p>
              ) : (
                <div className="space-y-4">
                  {Object.entries(groupedAssets).map(([type, typeAssets]) => (
                    <div key={type}>
                      <h4 className="font-semibold text-sm text-gray-700 mb-2">{type}</h4>
                      <div className="space-y-2">
                        {typeAssets.map((asset) => (
                          <div key={asset.asset_id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900">{asset.name}</p>
                              {asset.url && (
                                <a href={asset.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1">
                                  View Asset
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                              {asset.notes && <p className="text-xs text-gray-600 mt-1">{asset.notes}</p>}
                            </div>
                            {hasPermission(PERMISSIONS.MANAGE_ASSETS) && (
                              <Button size="sm" variant="ghost" onClick={() => handleDeleteAsset(asset.asset_id)}>
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Support Tickets Section */}
          <ClientSupportTab 
            clientId={client.client_id}
            isAdmin={isAdmin}
          />

          {/* Overview Card (existing) */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-gray-400" />
                <a href={`mailto:${client.primary_contact_email}`} className="text-blue-600 hover:underline">
                  {client.primary_contact_email}
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4 text-gray-400" />
                <a href={`tel:${client.primary_contact_phone}`} className="text-blue-600 hover:underline">
                  {client.primary_contact_phone}
                </a>
              </div>
              {client.business_website && (
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-gray-400" />
                  <a href={client.business_website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {client.business_website}
                  </a>
                </div>
              )}
              {client.niche_or_service_type && (
                <div className="flex items-center gap-2 text-sm">
                  <Briefcase className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-700 capitalize">{client.niche_or_service_type.replace('_', ' ')}</span>
                </div>
              )}
              {client.assigned_staff_username && (
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-700">Assigned: {client.assigned_staff_username}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Internal Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Internal Notes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add an internal note..."
                  rows={3}
                />
                <Button 
                  onClick={handleAddNote} 
                  disabled={!noteText.trim() || saving}
                  size="sm"
                >
                  {saving ? 'Adding...' : 'Add Note'}
                </Button>
              </div>

              {notes.length > 0 && (
                <div className="space-y-3 mt-4">
                  {notes.map((note, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{note.text}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        {note.added_by} • {formatDate(note.added_at)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Audit Log (Admin only) */}
          {isAdmin && auditLogs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Activity History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {auditLogs.map((log, index) => (
                    <div key={index} className="text-sm py-2 border-b last:border-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <span className="font-medium text-gray-900">{log.staff_username}</span>
                          <span className="text-gray-600 mx-2">•</span>
                          <span className="text-gray-700">{log.details}</span>
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {formatDate(log.timestamp)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Modals */}
      {showInvoiceModal && (
        <AddInvoiceModal
          clientId={client.client_id}
          isOpen={showInvoiceModal}
          onClose={() => setShowInvoiceModal(false)}
          onSuccess={() => {
            setShowInvoiceModal(false);
            fetchClientDetail();
          }}
        />
      )}

      {showTaskModal && (
        <AddTaskModal
          clientId={client.client_id}
          isOpen={showTaskModal}
          onClose={() => setShowTaskModal(false)}
          onSuccess={() => {
            setShowTaskModal(false);
            fetchClientDetail();
          }}
        />
      )}

      {showAssetModal && (
        <AddAssetModal
          clientId={client.client_id}
          isOpen={showAssetModal}
          onClose={() => setShowAssetModal(false)}
          onSuccess={() => {
            setShowAssetModal(false);
            fetchClientDetail();
          }}
        />
      )}

      {showPropertiesModal && (
        <EditDigitalPropertiesModal
          clientId={client.client_id}
          currentProperties={digitalProperties}
          isOpen={showPropertiesModal}
          onClose={() => setShowPropertiesModal(false)}
          onSuccess={() => {
            setShowPropertiesModal(false);
            fetchClientDetail();
          }}
        />
      )}
    </div>
  );
}
